import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { UnauthorizedFunction } from '../../utils/UtilsFunction';

/**
 * Async thunk to fetch recent projects from the API.
 * Calls the UnauthorizedFunction if the response is unsuccessful.
 */
export const get_recent_project_data = createAsyncThunk(
  'recent_project',
  async (_, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem('token'); // Retrieve token from local storage
      const response = await fetch(
        process.env.REACT_APP_BASE_URL + 'experience/get-recent-projects',
        {
          method: 'GET',
          headers: {
            Authorization: `Bearer ${token}`, // Include authorization header
          },
        }
      );
      const res = await response.json();

      if (!res.success) {
        UnauthorizedFunction(res); // Handle unauthorized access
      }

      return res;
    } catch (error) {
      console.error('Error fetching recent projects:', error);
      return rejectWithValue(error.message);
    }
  }
);

/**
 * Async thunk to fetch industry templates from the API.
 * Calls the UnauthorizedFunction if the response is unsuccessful.
 */
export const get_industry_templates_data = createAsyncThunk(
  'industry_templates',
  async (_, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem('token'); // Retrieve token from local storage
      const response = await fetch(
        process.env.REACT_APP_BASE_URL + 'experience/get-industry-templates',
        {
          method: 'GET',
          headers: {
            Authorization: `Bearer ${token}`, // Include authorization header
          },
        }
      );
      const res = await response.json();

      if (!res.success) {
        UnauthorizedFunction(res); // Handle unauthorized access
      }

      return res;
    } catch (error) {
      console.error('Error fetching industry templates:', error);
      return rejectWithValue(error.message);
    }
  }
);

/**
 * Async thunk to fetch recent tutorials from the API.
 * Calls the UnauthorizedFunction if the response is unsuccessful.
 */
export const get_recent_tutorials_data = createAsyncThunk(
  'recent_tutorials',
  async (_, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem('token'); // Retrieve token from local storage
      const response = await fetch(
        process.env.REACT_APP_BASE_URL + 'tutorials/recent',
        {
          method: 'GET',
          headers: {
            Authorization: `Bearer ${token}`, // Include authorization header
          },
        }
      );
      const res = await response.json();

      if (!res.success) {
        UnauthorizedFunction(res); // Handle unauthorized access
      }

      return res;
    } catch (error) {
      console.error('Error fetching recent tutorials:', error);
      return rejectWithValue(error.message);
    }
  }
);

/**
 * Initial state for the Redux slice.
 * Stores data for recent projects, industry templates, and recent tutorials,
 * along with their respective loading states.
 */
const initialState = {
  industry_templates: [],
  industry_templates_loading: false,
  recent_project: [],
  recent_project_loading: false,
  recent_tutorials: [],
  recent_tutorials_loading: false,
  showChatInterface: false, // State to control chat interface visibility
};

/**
 * Redux slice for managing home page data.
 * Uses extraReducers to handle async thunk actions.
 */
const homeSlice = createSlice({
  name: 'data',
  initialState,
  reducers: {
    // Reducer to toggle chat interface visibility
    toggleChatInterface: (state, action) => {
      state.showChatInterface = action.payload; // Update visibility based on payload
    },
  },
  extraReducers: (builder) => {
    builder
      // Handling Recent Projects API Call
      .addCase(get_recent_project_data.pending, (state) => {
        state.recent_project_loading = true; // Set loading state to true
      })
      .addCase(get_recent_project_data.fulfilled, (state, action) => {
        state.recent_project_loading = false;
        state.recent_project = action.payload?.success
          ? action.payload.data // Store fetched data if successful
          : [];
      })
      .addCase(get_recent_project_data.rejected, (state) => {
        state.recent_project_loading = false; // Set loading state to false on failure
      });

    // Handling Industry Templates API Call
    builder
      .addCase(get_industry_templates_data.pending, (state) => {
        state.industry_templates_loading = true; // Set loading state to true
      })
      .addCase(get_industry_templates_data.fulfilled, (state, action) => {
        state.industry_templates_loading = false;
        state.industry_templates = action.payload?.success
          ? action.payload.data // Store fetched data if successful
          : [];
      })
      .addCase(get_industry_templates_data.rejected, (state) => {
        state.industry_templates_loading = false; // Set loading state to false on failure
      });

    // Handling Recent Tutorials API Call
    builder
      .addCase(get_recent_tutorials_data.pending, (state) => {
        state.recent_tutorials_loading = true; // Set loading state to true
      })
      .addCase(get_recent_tutorials_data.fulfilled, (state, action) => {
        state.recent_tutorials_loading = false;
        state.recent_tutorials = action.payload?.success
          ? action.payload.tutorials // Store fetched data if successful
          : [];
      })
      .addCase(get_recent_tutorials_data.rejected, (state) => {
        state.recent_tutorials_loading = false; // Set loading state to false on failure
      });
  },
});

export const { toggleChatInterface } = homeSlice.actions;

export default homeSlice.reducer;
